/**
 * AdminOrgsListPage (Iter 9.39c)
 *
 * Lists every org with member_count + linked_dashboards, searchable.
 * Expandable row shows linked users (via org_members) + linked dashboards.
 */
import { useEffect, useState, useCallback } from "react";
import { Building2, Users as UsersIcon, LayoutDashboard, ExternalLink } from "lucide-react";
import MasterDataTable from "@/components/admin/MasterDataTable";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

export default function AdminOrgsListPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/organizations`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setOrgs(d.organizations || []); }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "name", label: "Organization", render: (r) => (
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-gray-900" data-testid={`org-name-${r.org_id}`}>{r.name || "(no name)"}</span>
      </div>
    ) },
    { key: "primary_category", label: "Category", render: (r) => r.primary_category ? <Badge variant="outline" className="text-[10px] capitalize">{r.primary_category}</Badge> : <span className="text-gray-300">-</span> },
    { key: "member_count", label: "Members", render: (r) => (
      <span className="inline-flex items-center gap-1 text-gray-700"><UsersIcon className="w-3.5 h-3.5 text-gray-400" />{r.member_count || 0}</span>
    ) },
    { key: "linked_dashboards", label: "Dashboards", render: (r) => (
      <span className="inline-flex items-center gap-1 text-gray-700"><LayoutDashboard className="w-3.5 h-3.5 text-gray-400" />{r.linked_dashboards || 0}</span>
    ) },
    { key: "is_pilot", label: "Pilot", render: (r) => r.is_pilot ? <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px]">Pilot</Badge> : <span className="text-gray-300">-</span>, sortable: false },
  ];

  return (
    <div className="p-4 sm:p-6" data-testid="admin-orgs-list-page">
      <MasterDataTable
        title="Hosts"
        icon={Building2}
        description="Every org, linked to its owner/members and all host dashboards. Click a row to see full detail."
        loading={loading}
        rows={orgs}
        columns={columns}
        searchFields={["name", "primary_category"]}
        testid="orgs"
        renderRowDetail={(row) => <OrgDetail org={row} />}
        emptyHint="No organizations yet."
      />
    </div>
  );
}

function OrgDetail({ org }) {
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [m, e] = await Promise.all([
          fetch(`${API}/admin/organizations/${org.org_id}/members`, { headers: auth() }).then(r => r.ok ? r.json() : { members: [] }),
          fetch(`${API}/admin/organizations/${org.org_id}/events`, { headers: auth() }).then(r => r.ok ? r.json() : { events: [] }),
        ]);
        setMembers(m.members || []);
        setEvents(e.events || []);
      } finally { setLoading(false); }
    };
    load();
  }, [org.org_id]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`org-detail-${org.org_id}`}>
      <section>
        <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Members ({members.length})</h4>
        {loading ? <div className="text-xs text-gray-400">Loading…</div> : members.length === 0 ? <div className="text-xs text-gray-400">None</div> : (
          <ul className="space-y-1 text-sm">
            {members.slice(0, 15).map((m) => (
              <li key={m.user_id || m.member_id} className="flex items-center gap-2">
                <span className="text-gray-700">{m.name || m.email}</span>
                {m.role && <Badge variant="outline" className="text-[10px]">{m.role}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Linked dashboards ({events.length})</h4>
        {loading ? <div className="text-xs text-gray-400">Loading…</div> : events.length === 0 ? <div className="text-xs text-gray-400">None</div> : (
          <ul className="space-y-1 text-sm">
            {events.slice(0, 15).map((e) => (
              <li key={e.event_id} className="flex items-center gap-2">
                <span className="text-gray-700">{e.name || e.event_id}</span>
                {e.category && <Badge variant="outline" className="text-[10px] capitalize">{e.category}</Badge>}
                <a href={`/h/${e.event_id}`} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline inline-flex items-center text-xs ml-auto">
                  Preview <ExternalLink className="w-3 h-3 ml-0.5" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
